import classNames from "classnames";
import { isNumber } from "lodash";
import PropTypes from "prop-types";
import React, { useEffect, useRef } from "react";

import { ActionIcon } from "../../action_icon";
import { useCarousel } from "../use_carousel";
import css from "./dynamic_carousel.module.scss";

const gridGap = 5;
const getUnit = (widthCssString) => (widthCssString.includes("px") ? "px" : "%");

export const LeftArrow = ({ onClick, index, className }) => (
  <ActionIcon
    name="chevron-left"
    size="md"
    onClick={onClick}
    classes={{ root: classNames(className, { [css.iconDisabled]: index === 0 }) }}
  />
);

LeftArrow.propTypes = {
  onClick: PropTypes.func.isRequired,
  index: PropTypes.number.isRequired,
  className: PropTypes.string
};

export const RightArrow = ({ onClick, index, length, className }) => (
  <ActionIcon
    name="chevron-right"
    size="md"
    onClick={onClick}
    classes={{ root: classNames(className, { [css.iconDisabled]: index >= length - 1 }) }}
  />
);

RightArrow.propTypes = {
  onClick: PropTypes.func.isRequired,
  index: PropTypes.number.isRequired,
  length: PropTypes.number.isRequired,
  className: PropTypes.string
};

export const Actions = ({ onClickLeft, onClickRight, index, scrollLength, actionsClassName, label }) => (
  <div className={classNames(actionsClassName, css.actions)}>
    <LeftArrow onClick={onClickLeft} index={index} />
    {label ? <div>{label}</div> : <div className={css.separator} />}
    <RightArrow onClick={onClickRight} index={index} length={scrollLength} />
  </div>
);

Actions.propTypes = {
  onClickLeft: PropTypes.func.isRequired,
  onClickRight: PropTypes.func.isRequired,
  index: PropTypes.number.isRequired,
  scrollLength: PropTypes.number.isRequired,
  actionsClassName: PropTypes.string,
  label: PropTypes.node
};

// TODO: [ENG-3090] Refactor DynamicCarousel component to be reusable
// or remove it if can be replaced with other Carousel component
export const DynamicCarousel = ({ items, index, className }) => {
  const containerRef = useRef();
  const {
    activeIndex: innerIndex,
    slideBack,
    slideForward,
    slideTo
  } = useCarousel({ maxIndex: items.length - 1 });

  const widths = items.map((i) => i.width);

  const totalWidths = widths.reduce(
    (acc, width) => {
      const unit = getUnit(width);
      const value = Number(width.replace(unit, ""));
      return { ...acc, [unit]: acc[unit] + value };
    },
    { "px": 0, "%": 0 }
  );

  const gridTemplateColumns = widths
    .map((width) =>
      getUnit(width) === "px"
        ? width
        : `calc(${100 / (totalWidths["%"] / Number(width.replace("%", "")))}% - ${
            totalWidths.px / Number(width.replace("%", ""))
          }px)`
    )
    .join(" ");
  const totalWidth = `calc(${widths.join(" + ")})`;

  useEffect(() => {
    if (isNumber(index)) {
      slideTo(index);
    }
  }, [index, slideTo]);

  return (
    <div className={className}>
      {!isNumber(index) && (
        <Actions
          onClickLeft={slideBack}
          onClickRight={slideForward}
          index={innerIndex}
          scrollLength={items.length}
        />
      )}

      <div className={css.container}>
        <div
          className={css.movingContainer}
          style={{
            transform:
              innerIndex > 0
                ? `translateX(calc(-${items
                    .slice(0, innerIndex)
                    .map((item) => item.width)
                    .join(" - ")} - ${gridGap * innerIndex}px))`
                : "translateX(0px)"
          }}
        >
          <div
            ref={containerRef}
            style={{
              display: "grid",
              gridTemplateColumns,
              width: totalWidth,
              gridGap
            }}
          >
            {items.map((item, index) => (
              <div key={item.key || index} className={css.item}>
                {item.Component}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

DynamicCarousel.propTypes = {
  className: PropTypes.string,
  index: PropTypes.number,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      width: PropTypes.string.isRequired,
      Component: PropTypes.node.isRequired
    })
  ).isRequired
};
